// src/pages/CasinoLucky7ViewPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getAccessToken } from '../store/actions/auth';
import API_BASE_URL from '../config/baseUrl';
const ODDS_API_URL = "https://oddsapi.247idhub.com";

// Card back image (local asset)
const cardBackUrl = "/Images/casino/cardBack.png";

// Card images mapping
const cardImages = {
  1: '/Images/casino/1.jpg',
  2: '/Images/casino/2.jpg',
  3: '/Images/casino/3.jpg',
  4: '/Images/casino/4.jpg',
  5: '/Images/casino/5.jpg',
  6: '/Images/casino/6.jpg',
  7: '/Images/casino/7.jpg',
  8: '/Images/casino/8.jpg',
  9: '/Images/casino/9.jpg',
  10: '/Images/casino/10.jpg',
  11: '/Images/casino/11.jpg', // J
  12: '/Images/casino/12.jpg', // Q
  13: '/Images/casino/13.jpg', // K
};

// Flip Card Component
const FlipCard = ({ isFlipped, cardValue }) => {
  return (
    <div className="w-6 h-10 sm:w-8 sm:h-12 md:w-10 md:h-16 lg:w-12 lg:h-20 xl:w-12 xl:h-16 2xl:w-14 2xl:h-20 perspective-1000">
      <div 
        className={`relative w-full h-full transition-transform duration-500 transform-style-3d ${isFlipped ? 'rotate-y-180' : ''}`}
        style={{ transformStyle: 'preserve-3d' }}
      >
        <div 
          className="absolute w-full h-full backface-hidden"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <img src={cardBackUrl} alt="card back" className="w-full h-full rounded" />
        </div>
        <div 
          className="absolute w-full h-full backface-hidden rotate-y-180"
          style={{ 
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }}
        >
          <img 
            src={cardValue || cardBackUrl} 
            alt="card front" 
            className="w-full h-full rounded" 
          />
        </div>
      </div>
    </div>
  );
};

// Left Arrow Icon
const LeftArrowIcon = () => (
  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
    <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z"></path>
  </svg>
);

// Right Arrow Icon
const RightArrowIcon = () => (
  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
    <path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z"></path>
  </svg>
);

// Get today's date
const getTodayDate = () => {
  const today = new Date();
  return today.getFullYear() + '-' + 
         String(today.getMonth() + 1).padStart(2, '0') + '-' + 
         String(today.getDate()).padStart(2, '0');
};

// Map result codes to display letters
const getResultLetter = (result) => {
  // Based on the result codes: 0=T, 1=L, 2=H
  if (result === '0') return 'T';
  if (result === '1') return 'L';
  if (result === '2') return 'H';
  return result;
};

const CasinoLucky7ViewPage = () => {
  const navigate = useNavigate();
  const { tableId, gameName } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [currentRoundId, setCurrentRoundId] = useState('');
  const [gameData, setGameData] = useState(null);
  const [resultsHistory, setResultsHistory] = useState([]);
  const [liability, setLiability] = useState({});
  const [nonDeclareBets, setNonDeclareBets] = useState([]);
  const [completedGames, setCompletedGames] = useState([]);
  const [totalPnl, setTotalPnl] = useState(0);
  const [currentCard, setCurrentCard] = useState(null);
  const [timer, setTimer] = useState(0);
  
  // Pagination states
  const [nonDeclareCurrentPage, setNonDeclareCurrentPage] = useState(1);
  const [completedCurrentPage, setCompletedCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch game data (1 second interval)
  const fetchGameData = async () => {
    try {
      const response = await axios.get(`${ODDS_API_URL}/betfair_api/casino/data/meta-lucky7`);
      if (response.data && response.data.t1 && response.data.t1[0]) {
        setGameData(response.data);
        setCurrentRoundId(response.data.t1[0].mid);
        setCurrentCard(response.data.t1[0].C1);
        setTimer(response.data.t1[0].autotime || 0);
      }
    } catch (err) {
      console.error('Error fetching game data:', err);
    }
  };

  // Fetch results history (1 second interval)
  const fetchResultsHistory = async () => {
    try {
      const response = await axios.get(`${ODDS_API_URL}/betfair_api/casino/result/meta-lucky7`);
      if (response.data) {
        setResultsHistory(response.data.slice(0, 10));
      }
    } catch (err) {
      console.error('Error fetching results:', err);
    }
  };

  // Fetch liability
  const fetchLiability = async (roundId) => {
    if (!roundId) return;
    try {
      const token = getAccessToken();
      const response = await axios.post(
        `${API_BASE_URL}/casino/liability`,
        { roundId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status && response.data.data) {
        const liabilityMap = {};
        response.data.data.forEach(item => {
          liabilityMap[item.sid] = item.liability;
        });
        setLiability(liabilityMap);
      }
    } catch (err) {
      console.error('Error fetching liability:', err);
    }
  };

  // Fetch non-declare bets
  const fetchNonDeclareBets = async () => {
    try {
      const token = getAccessToken();
      const today = getTodayDate();
      const response = await axios.post(
        `${API_BASE_URL}/casino/casino-bet-list-admin`,
        {
          tableId: tableId || "53",
          fromDate: today,
          toDate: today,
          userId: "",
          isGameCompleted: false,
          sportId: 5015
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status && response.data.data) {
        setNonDeclareBets(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching non-declare bets:', err);
    }
  };

  // Fetch completed games
  const fetchCompletedGames = async () => {
    try {
      const token = getAccessToken();
      const response = await axios.post(
        `${API_BASE_URL}/ant-pro/get-casino-bets-by-tableid`,
        { tableId: tableId || "53", isActive: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status && response.data.data) {
        setCompletedGames(response.data.data);
        const total = response.data.data.reduce((sum, game) => sum + (game.pnl || 0), 0);
        setTotalPnl(total);
      }
    } catch (err) {
      console.error('Error fetching completed games:', err);
    }
  };

  // Initial load
  useEffect(() => {
    const initLoad = async () => {
      setLoading(true);
      await Promise.all([
        fetchGameData(),
        fetchResultsHistory(),
        fetchNonDeclareBets(),
        fetchCompletedGames()
      ]);
      setLoading(false);
    };
    initLoad();
  }, [tableId]);

  // Fetch liability when roundId changes
  useEffect(() => {
    if (currentRoundId) {
      fetchLiability(currentRoundId);
    }
  }, [currentRoundId]);

  // Set up 1-second polling
  useEffect(() => {
    const interval = setInterval(() => {
      fetchGameData();
      fetchResultsHistory();
      if (currentRoundId) {
        fetchLiability(currentRoundId);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [currentRoundId]);

  // Get player data with liability
  const getPlayerData = (sid) => {
    if (!gameData || !gameData.t2) return { rate: '0', liability: 0, name: '', gstatus: '1' };
    const player = gameData.t2.find(p => p.sid === sid);
    return {
      name: player?.nat || '',
      rate: player?.rate || '0',
      liability: liability[sid] || 0,
      gstatus: player?.gstatus || '1'
    };
  };

  // Get card image for specific card number
  const getCardImage = (cardNum) => {
    return cardImages[cardNum] || cardImages[1];
  };

  // Pagination for Non-Declare Bets
  const nonDeclareTotal = nonDeclareBets.length;
  const nonDeclareTotalPages = Math.ceil(nonDeclareTotal / itemsPerPage);
  const nonDeclareStart = (nonDeclareCurrentPage - 1) * itemsPerPage;
  const nonDeclareEnd = nonDeclareStart + itemsPerPage;
  const currentNonDeclareBets = nonDeclareBets.slice(nonDeclareStart, nonDeclareEnd);

  // Pagination for Completed Games
  const completedTotal = completedGames.length;
  const completedTotalPages = Math.ceil(completedTotal / itemsPerPage);
  const completedStart = (completedCurrentPage - 1) * itemsPerPage;
  const completedEnd = completedStart + itemsPerPage;
  const currentCompletedGames = completedGames.slice(completedStart, completedEnd);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Main Card */}
      <div className="bg-white rounded shadow border border-gray-200 mb-4">
        {/* Header */}
        <div className="bg-gray-600 px-5 py-3 flex items-center justify-between">
          <span className="text-xl text-white font-normal">
            {decodeURIComponent(gameName || 'LUCKY 7 - A')}
          </span>
          <button 
            onClick={() => navigate(-1)}
            className="bg-blue-500 text-white px-4 py-2 hover:bg-blue-600"
          >
            Back
          </button>
        </div>

        <div className="p-4">
          {/* Video and Odds Section */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
            {/* Left Side - Video & Card */}
            <div className="xl:col-span-7">
              {/* Match ID Bar */}
              <div className="bg-gray-700 px-3 py-2 flex justify-between items-center text-white mb-2">
                <span>Match ID: {currentRoundId}</span>
                <span>{timer}</span>
              </div>

              {/* Casino Video */}
              <div className="relative bg-black" style={{ height: '300px' }}>
                <iframe
                  title="Casino Video"
                  className="w-full h-full"
                  src="https://casino.loki7exch.com/route/?id=3058"
                />
                
                {/* Card Overlay */}
                <div className="absolute top-4 left-4">
                  <div className="mb-2">
                    <div className="text-white text-sm mb-2">Card</div>
                    <div className="flex gap-2">
                      <FlipCard 
                        isFlipped={currentCard !== null} 
                        cardValue={currentCard ? getCardImage(parseInt(currentCard)) : null}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Last 10 Winners */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span>Last 10 winners:</span>
                {resultsHistory.map((item, idx) => (
                  <span 
                    key={idx}
                    className="inline-block bg-blue-500 text-white px-3 py-1 text-sm"
                  >
                    {getResultLetter(item.result)}
                  </span>
                ))}
              </div>
            </div>

            {/* Right Side - Player Rates Table */}
            <div className="xl:col-span-5">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300 text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 p-2 text-left">Player Name</th>
                      <th className="border border-gray-300 p-2 text-left">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Low Card */}
                    <tr>
                      <td className="border border-gray-300 p-2">
                        <div>
                          <p>LOW Card</p>
                          <p className="font-bold text-black">
                            {getPlayerData('1').liability.toFixed(2)}
                          </p>
                        </div>
                      </td>
                      <td className="border border-gray-300 p-2">
                        {getPlayerData('1').rate}
                      </td>
                    </tr>

                    {/* High Card */}
                    <tr>
                      <td className="border border-gray-300 p-2">
                        <div>
                          <p>HIGH Card</p>
                          <p className="font-bold text-black">
                            {getPlayerData('2').liability.toFixed(2)}
                          </p>
                        </div>
                      </td>
                      <td className="border border-gray-300 p-2">
                        {getPlayerData('2').rate}
                      </td>
                    </tr>

                    {/* Even */}
                    <tr>
                      <td className="border border-gray-300 p-2">
                        <div>
                          <p>Even</p>
                          <p className="font-bold text-black">
                            {getPlayerData('3').liability.toFixed(2)}
                          </p>
                        </div>
                      </td>
                      <td className="border border-gray-300 p-2">
                        {getPlayerData('3').rate}
                      </td>
                    </tr>

                    {/* Odd */}
                    <tr>
                      <td className="border border-gray-300 p-2">
                        <div>
                          <p>Odd</p>
                          <p className="font-bold text-black">
                            {getPlayerData('4').liability.toFixed(2)}
                          </p>
                        </div>
                      </td>
                      <td className="border border-gray-300 p-2">
                        {getPlayerData('4').rate}
                      </td>
                    </tr>

                    {/* Red */}
                    <tr>
                      <td className="border border-gray-300 p-2">
                        <div>
                          <p>Red</p>
                          <p className="font-bold text-black">
                            {getPlayerData('5').liability.toFixed(2)}
                          </p>
                        </div>
                      </td>
                      <td className="border border-gray-300 p-2">
                        {getPlayerData('5').rate}
                      </td>
                    </tr>

                    {/* Black */}
                    <tr>
                      <td className="border border-gray-300 p-2">
                        <div>
                          <p>Black</p>
                          <p className="font-bold text-black">
                            {getPlayerData('6').liability.toFixed(2)}
                          </p>
                        </div>
                      </td>
                      <td className="border border-gray-300 p-2">
                        {getPlayerData('6').rate}
                      </td>
                    </tr>

                    {/* Card 1 */}
                    <tr>
                      <td className="border border-gray-300 p-2">
                        <div>
                          <p>Card 1</p>
                          <p className="font-bold text-black">
                            {getPlayerData('7').liability.toFixed(2)}
                          </p>
                        </div>
                      </td>
                      <td className="border border-gray-300 p-2">
                        {getPlayerData('7').rate}
                      </td>
                    </tr>

                    {/* Cards 2-13 with images */}
                    {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map((cardNum) => {
                      const sid = String(cardNum + 6); // sid starts from 8 for Card 2
                      const cardName = cardNum <= 10 ? `Card ${cardNum}` : 
                                      cardNum === 11 ? 'Card J' : 
                                      cardNum === 12 ? 'Card Q' : 'Card K';
                      return (
                        <tr key={cardNum}>
                          <td className="border border-gray-300 p-2">
                            <div>
                              <img 
                                src={getCardImage(cardNum)} 
                                alt={cardName}
                                className="w-[30px] h-[45px] block"
                              />
                              <p className="font-bold text-black mt-1">
                                {getPlayerData(sid).liability.toFixed(2)}
                              </p>
                            </div>
                          </td>
                          <td className="border border-gray-300 p-2">
                            {getPlayerData(sid).rate}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* NonDeclare Bets */}
      <div className="bg-white rounded shadow border border-gray-200 mb-4">
        <div className="bg-gray-600 px-5 py-3">
          <span className="text-xl text-white font-normal">
            NonDeclare Bets - [{nonDeclareTotal}]
          </span>
        </div>
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-300 p-2 text-left">Client</th>
                  <th className="border border-gray-300 p-2 text-left">RoundId</th>
                  <th className="border border-gray-300 p-2 text-left">Player</th>
                  <th className="border border-gray-300 p-2 text-left">Winner</th>
                  <th className="border border-gray-300 p-2 text-left">Stake</th>
                  <th className="border border-gray-300 p-2 text-left">Profit</th>
                  <th className="border border-gray-300 p-2 text-left">Loss</th>
                </tr>
              </thead>
              <tbody>
                {currentNonDeclareBets.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="border border-gray-300 p-8 text-center text-gray-500">
                      No undeclared bets
                    </td>
                  </tr>
                ) : (
                  currentNonDeclareBets.map((bet, idx) => (
                    <tr key={idx}>
                      <td className="border border-gray-300 p-2">{bet.userId}</td>
                      <td className="border border-gray-300 p-2">{bet.marketId}</td>
                      <td className="border border-gray-300 p-2">{bet.selectionName}</td>
                      <td className="border border-gray-300 p-2">
                        <span className="text-green-600">Not Declear</span>
                      </td>
                      <td className="border border-gray-300 p-2">{bet.amount}</td>
                      <td className="border border-gray-300 p-2">{bet.profit || ''}</td>
                      <td className="border border-gray-300 p-2">{bet.loss || ''}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {nonDeclareTotalPages > 1 && (
            <div className="flex justify-end mt-3">
              <ul className="flex items-center gap-1">
                <li>
                  <button
                    onClick={() => setNonDeclareCurrentPage(p => Math.max(1, p - 1))}
                    disabled={nonDeclareCurrentPage === 1}
                    className="px-2 py-1 border border-gray-300 disabled:opacity-50"
                  >
                    <LeftArrowIcon />
                  </button>
                </li>
                <li>
                  <span className="px-3 py-1 bg-blue-500 text-white">
                    {nonDeclareCurrentPage}
                  </span>
                </li>
                <li>
                  <button
                    onClick={() => setNonDeclareCurrentPage(p => Math.min(nonDeclareTotalPages, p + 1))}
                    disabled={nonDeclareCurrentPage === nonDeclareTotalPages}
                    className="px-2 py-1 border border-gray-300 disabled:opacity-50"
                  >
                    <RightArrowIcon />
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Completed Games */}
      <div className="bg-white rounded shadow border border-gray-200">
        <div className="bg-gray-600 px-5 py-3 flex justify-between items-center">
          <span className="text-xl text-white font-normal">
            {decodeURIComponent(gameName || 'LUCKY 7 - A')}
          </span>
          <h1 className="text-white text-lg">
            Total: <span className={totalPnl >= 0 ? 'text-green-400' : 'text-red-400'}>
              {totalPnl.toFixed(2)}
            </span>
          </h1>
        </div>
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-300 p-2 text-left">Game ID</th>
                  <th className="border border-gray-300 p-2 text-left">Started AT</th>
                  <th className="border border-gray-300 p-2 text-left">Plus/Minus</th>
                </tr>
              </thead>
              <tbody>
                {currentCompletedGames.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="border border-gray-300 p-8 text-center text-gray-500">
                      No completed games
                    </td>
                  </tr>
                ) : (
                  currentCompletedGames.map((game, idx) => (
                    <tr key={idx}>
                      <td className="border border-gray-300 p-2">{game.roundId}</td>
                      <td className="border border-gray-300 p-2">
                        <span>-</span>
                      </td>
                      <td className="border border-gray-300 p-2">
                        <span className={game.pnl >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {game.pnl.toFixed(2)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {completedTotalPages > 1 && (
            <div className="flex justify-end mt-3">
              <ul className="flex items-center gap-1">
                <li>
                  <button
                    onClick={() => setCompletedCurrentPage(p => Math.max(1, p - 1))}
                    disabled={completedCurrentPage === 1}
                    className="px-2 py-1 border border-gray-300 disabled:opacity-50"
                  >
                    <LeftArrowIcon />
                  </button>
                </li>
                <li>
                  <span className="px-3 py-1 bg-blue-500 text-white">
                    {completedCurrentPage}
                  </span>
                </li>
                <li>
                  <button
                    onClick={() => setCompletedCurrentPage(p => Math.min(completedTotalPages, p + 1))}
                    disabled={completedCurrentPage === completedTotalPages}
                    className="px-2 py-1 border border-gray-300 disabled:opacity-50"
                  >
                    <RightArrowIcon />
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CasinoLucky7ViewPage;
